import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { confirmDiscardAsync } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AlertController } from 'providers/alert-controller';
import { AuthService } from 'services/auth.service';
import { getMyApp } from 'app/app-interface';
import { ToastController } from 'providers/toast-controller';
import { CoachNotifyService } from 'services/coach-notify.service';
import { CoachNotifyMessageParams } from '@cf-shared/coach-notify-interface';
import { SendCoachMessagePageParams, ISendCoachMessagePage } from './send-coach-message-interface';
import { SendFeedbackType } from '@cf-shared/send-feedback-interface';
import { CsScreenshotAnnotatorComponent } from 'components/cs-screenshot-annotator/cs-screenshot-annotator';

enum MessageState {
  WriteMessage,
  WithScreenshot,
}

@Component({
  selector: 'page-send-coach-message',
  templateUrl: 'send-coach-message.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendCoachMessagePage extends CsPage<SendCoachMessagePageParams> {
  protected LL = LL;

  @ViewChild(CsScreenshotAnnotatorComponent, { static: false }) annotator: CsScreenshotAnnotatorComponent;

  /** the captured screenshot to annotate, taken off the round trip on the way in */
  screenshotDataUrl: string | undefined;

  MessageState = MessageState;
  state = MessageState.WriteMessage;
  messageText = '';
  private isSending = false;

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    private toastCtrl: ToastController,
    private coachNotify: CoachNotifyService,
    private alertCtrl: AlertController,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewWillEnter() {
    // if returning from screenshot capture
    if (getMyApp().screenshotTrip.hasScreenshot) {
      this.state = MessageState.WithScreenshot;
      // take the captured image here, where the trip is already being read, and
      // hand it to the annotator as an input. taking it also clears it
      this.screenshotDataUrl = getMyApp().screenshotTrip.takeScreenshot();
    }
    // restore text typed before the user left to take a screenshot. this must not
    // depend on the capture having worked: a capture that failed comes back here
    // with no image, and only reading the stash when an image existed threw the
    // typed message away, with nothing telling the user so. the trip hands the text
    // only to the page that stashed it, which keeps this page off the feedback page's
    // stash
    const stashedText = getMyApp().screenshotTrip.takeTextFor(ISendCoachMessagePage);
    if (stashedText !== undefined) {
      this.messageText = stashedText;
    }
    this.cdRef.markForCheck();
  }

  async ionViewDidEnter() {
    // the annotator is only on screen in the screenshot state, and it is started
    // from here rather than from its own lifecycle hook because it sizes the
    // canvas by measuring where this page's form ended up
    this.annotator?.initAfterEnter();
    getMyApp().hideScreenshotButton();
    this.cdRef.markForCheck();
  }

  onAddScreenshotClicked() {
    getMyApp().screenshotTrip.stashText(ISendCoachMessagePage, this.messageText);
    getMyApp().screenshotTrip.returnTo(ISendCoachMessagePage);
    getMyApp().showScreenshotButton(SendFeedbackType.Unknown);
    // the text is stashed above and restored on the way back in, so this trip
    // loses nothing and must not raise the discard question
    this.leaveWithoutAsking.set();
    void this.navCtrl.popAsync();
  }

  async onSendClicked() {
    if (this.isSending || !this.messageText.trim()) return;
    this.isSending = true;

    let screenshotDataUrl: string | undefined;
    // no `&& this.annotator` guard on purpose: the annotator is on screen in exactly
    // this state, so a missing one is a real fault and should surface rather than
    // quietly send the message without the picture the user drew on
    if (this.state === MessageState.WithScreenshot) {
      screenshotDataUrl = this.annotator.getAnnotatedImageDataUrl();
    }

    const params: CoachNotifyMessageParams = {
      type: 'clientMessage',
      clientDisplayName: this.auth.user.name || this.auth.user.uid,
      clientEmail: this.auth.user.email,
      message: this.messageText.trim(),
      screenshotDataUrl,
    };

    void this.coachNotify.queueNotificationAsync(params);
    this.leaveWithoutAsking.set();

    this.toastCtrl.showToast({
      message: LL.sendCoachMessage.sentDescription(),
      showCloseButton: true,
      duration: 5000,
    });

    await this.navCtrl.popAsync();
  }

  async onCloseClicked() {
    if (this.isSending) return;
    // the close button goes through the same guard as the back button, so the
    // user is asked once, in one place, however they leave
    await this.navCtrl.popAsync();
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the close button,
   * the browser back button, Android's hardware back button or a swipe back.
   * Without it, a message the user had typed to their coach — and any drawing
   * they had made on the screenshot — was thrown away in silence (#5058).
   *
   * There is no saving a half-written message, so the only real question is
   * whether they meant to discard it.
   */
  async ionViewCanLeave(): Promise<boolean> {
    // two paths arm this, having already settled what happens to the message:
    // sending it, and the hop out to take a screenshot. That hop deliberately POPS
    // this page and comes back to it, having stashed the typed text first, so
    // asking there would interrupt a round trip that loses nothing.
    //
    // the close button is deliberately NOT one of them — it pops like any other
    // exit and so goes through the guard, which is what makes the user get the
    // same question once, however they leave.
    if (this.leaveWithoutAsking.consume()) return true;
    if (!this.messageText.trim()) return true;
    return confirmDiscardAsync(this.alertCtrl, {
      title: LL.unsavedChanges.discard.coachMessageTitle(),
      message: LL.unsavedChanges.discard.coachMessageMessage(),
    });
  }

}
